// MODELUL 3D ÎNAINTE DE PASUL 7.
//
// Scena 3D a structurii se desenează din rezultatul Pasului 7, iar Pasul 7 e
// zeci de minute de mesh și eigen. Geometria din care se desenează, însă, e
// gata mai devreme: stâlpii și grinzile vin din Pasul 4, fundația din Pasul 6.
// Serverul poate deci CONSTRUI modelul fără să-l analizeze
// (`steps/model_fem/schita`), în secunde — și de acolo se vede clădirea.
//
// Cine are nevoie de asta:
//
//   * SCHIȚA de structură, rularea pe care platforma o cere singură după
//     fiecare arhitectură: ea se oprește înaintea Pasului 7, deci fără cererea
//     de aici n-ar avea nicio vedere 3D (v. `structure/chain.sketch_steps`);
//   * orice soluție în lucru, între Pasul 4 și Pasul 7: până acum ecranul
//     spunea «modelul apare după rularea lanțului» și atât.
//
// Ce se vede e o GEOMETRIE, nu un verdict: `verificare_modala.neanalizat` e pe
// rezultat, iar ecranele o spun pe el (v. `Studio.tsx`, `StepFem.tsx`).

import { useEffect, useState } from "react";

import { getSchitaFem } from "./apiStructura";
import type { FemResult, StepRezumat, StepState } from "./tipuri";

export type StareSchita = "inactiva" | "se_incarca" | "gata" | "lipsa";

/** Cât se așteaptă între două întrebări «s-a construit?». Construcția ține
 *  5–30 s pe un corp mare și peste un minut pe ansamblu, deci nici prea des
 *  (ar ține un worker ocupat degeaba), nici prea rar. */
const REINCERCARE_MS = 4000;

/** Modelul construit-dar-neanalizat al unui spațiu, când Pasul 7 n-are
 *  rezultat și Pasul 4 are.
 *
 *  `inactiva` = nu e nevoie de el (Pasul 7 a rulat, sau rezultatul lui e pe
 *  drum, sau Pasul 4 n-a rulat încă), deci nu se cere nimic. */
export function useSchitaFem(
  projectId: string,
  cloneId: string | null | undefined,
  steps: Record<string, StepState>,
  rezumate: Record<string, StepRezumat> | null | undefined,
  /** `false` = ecranul nu arată modelul acum, deci nu se cere nimic. Modelul
   *  se construiește pe server la cerere (secunde de CPU), deci nu se cere de
   *  pe o pagină care nu-l desenează. */
  activ = true,
): { schita: FemResult | null; stare: StareSchita } {
  const pas7 = steps["model_fem"];
  // Pasul 4 e sursa geometriei; versiunea lui e și cheia de cache — un Pasul 4
  // rerulat dă alt model la aceeași adresă.
  const versiune = rezumate?.["model_structura"]?.updated_at
    ?? steps["model_structura"]?.updated_at ?? null;
  const are7 = !!pas7?.result
    || !!rezumate?.["model_fem"]?.are_rezultat;
  const are4 = !!steps["model_structura"]?.result
    || !!rezumate?.["model_structura"]?.are_rezultat;
  const cerem = activ && !are7 && are4 && !!versiune;

  const [schita, setSchita] = useState<FemResult | null>(null);
  const [stare, setStare] = useState<StareSchita>("inactiva");

  useEffect(() => {
    if (!cerem) {
      setSchita(null);
      setStare("inactiva");
      return;
    }
    let renunta = false;
    let ceas: ReturnType<typeof setTimeout> | undefined;
    setStare("se_incarca");
    const cere = () => {
      getSchitaFem(projectId, cloneId ?? null, versiune)
        .then((r) => {
          if (renunta) return;
          // 202: serverul construiește; ecranul ține scheletul și revine
          if (r?.se_construieste || !r?.model) {
            ceas = setTimeout(cere, REINCERCARE_MS);
            return;
          }
          setSchita(r as unknown as FemResult);
          setStare("gata");
        })
        .catch(() => {
          if (renunta) return;
          setSchita(null);
          setStare("lipsa");
        });
    };
    cere();
    return () => { renunta = true; if (ceas) clearTimeout(ceas); };
  }, [projectId, cloneId, versiune, cerem]);

  return { schita, stare };
}
